// PDF Generation Utility for Math Exercises
// Generates PDFs for individual questions or all questions in a topic

import { jsPDF } from 'jspdf';

// Helper function to clean LaTeX expressions for PDF
const cleanMathExpression = (text) => {
  if (!text) return '';
  
  // Debug logging
  console.log('🧽 Cleaning expression:', text);
  
  // First handle special cases and complex LaTeX structures
  let cleanedText = text
    // Handle display and inline math delimiters
    .replace(/\$\$([^$]+?)\$\$/g, '$1')  // Display math
    .replace(/\$([^$]+?)\$/g, '$1')      // Inline math
    
    // Handle fractions
    .replace(/\\frac\{([^}]+?)\}\{([^}]+?)\}/g, '($1)/($2)')
    
    // Handle square roots and nth roots
    .replace(/\\sqrt\{([^}]+?)\}/g, '√($1)')
    .replace(/\\sqrt\[(\d+)\]\{([^}]+?)\}/g, '($2)^(1/$1)')
    
    // Handle powers and subscripts
    .replace(/\^(\d+)/g, '^$1')
    .replace(/\^{([^}]+?)}/g, '^($1)')
    .replace(/_(\d+)/g, '_$1')
    .replace(/_{([^}]+?)}/g, '_($1)')
    
    // Handle mathematical operators
    .replace(/\\times/g, '×')
    .replace(/\\cdot/g, '·')
    .replace(/\\div/g, '÷')
    .replace(/\\pm/g, '±')
    .replace(/\\mp/g, '∓')
    
    // Handle inequalities
    .replace(/\\leq/g, '≤')
    .replace(/\\geq/g, '≥')
    .replace(/\\neq/g, '≠')
    .replace(/\\approx/g, '≈')
    .replace(/\\equiv/g, '≡')
    .replace(/\\sim/g, '~')
    
    // Handle sets and logic
    .replace(/\\in/g, '∈')
    .replace(/\\notin/g, '∉')
    .replace(/\\subset/g, '⊂')
    .replace(/\\supset/g, '⊃')
    .replace(/\\cup/g, '∪')
    .replace(/\\cap/g, '∩')
    .replace(/\\emptyset/g, '∅')
    .replace(/\\mathbb\{R\}/g, 'ℝ')
    .replace(/\\mathbb\{N\}/g, 'ℕ')
    .replace(/\\mathbb\{Z\}/g, 'ℤ')
    .replace(/\\mathbb\{Q\}/g, 'ℚ')
    
    // Handle infinity and limits
    .replace(/\\infty/g, '∞')
    .replace(/\\to/g, '→')
    .replace(/\\rightarrow/g, '→')
    .replace(/\\leftarrow/g, '←')
    
    // Handle Greek letters
    .replace(/\\alpha/g, 'α')
    .replace(/\\beta/g, 'β')
    .replace(/\\gamma/g, 'γ')
    .replace(/\\delta/g, 'δ')
    .replace(/\\epsilon/g, 'ε')
    .replace(/\\zeta/g, 'ζ')
    .replace(/\\eta/g, 'η')
    .replace(/\\theta/g, 'θ')
    .replace(/\\iota/g, 'ι')
    .replace(/\\kappa/g, 'κ')
    .replace(/\\lambda/g, 'λ')
    .replace(/\\mu/g, 'μ')
    .replace(/\\nu/g, 'ν')
    .replace(/\\xi/g, 'ξ')
    .replace(/\\pi/g, 'π')
    .replace(/\\rho/g, 'ρ')
    .replace(/\\sigma/g, 'σ')
    .replace(/\\tau/g, 'τ')
    .replace(/\\upsilon/g, 'υ')
    .replace(/\\phi/g, 'φ')
    .replace(/\\chi/g, 'χ')
    .replace(/\\psi/g, 'ψ')
    .replace(/\\omega/g, 'ω')
    
    // Handle trigonometric functions
    .replace(/\\sin/g, 'sin')
    .replace(/\\cos/g, 'cos')
    .replace(/\\tan/g, 'tan')
    .replace(/\\cot/g, 'cot')
    .replace(/\\sec/g, 'sec')
    .replace(/\\csc/g, 'csc')
    
    // Handle logarithms
    .replace(/\\log/g, 'log')
    .replace(/\\ln/g, 'ln')
    
    // Handle system of equations
    .replace(/\\begin\{cases\}/g, '')
    .replace(/\\end\{cases\}/g, '')
    .replace(/\\\\/g, '\n')
    
    // Handle parentheses and brackets
    .replace(/\\left\(/g, '(')
    .replace(/\\right\)/g, ')')
    .replace(/\\left\[/g, '[')
    .replace(/\\right\]/g, ']')
    .replace(/\\left\{/g, '{')
    .replace(/\\right\}/g, '}')
    
    // Handle spacing
    .replace(/\\,/g, ' ')
    .replace(/\\;/g, ' ')
    .replace(/\\:/g, ' ')
    .replace(/\\!/g, '')
    .replace(/\\ /g, ' ')
    .replace(/\\quad/g, '    ')
    .replace(/\\qquad/g, '        ')
    
    // Handle line breaks
    .replace(/\\\\n/g, '\n')
    .replace(/\\n/g, '\n')
    
    // Clean up remaining backslashes and braces
    .replace(/\\([a-zA-Z]+)/g, '$1')  // Remove backslashes from remaining commands
    .replace(/\{([^}]*)\}/g, '$1')    // Remove remaining braces
    
    // Clean up multiple spaces and trim
    .replace(/\s+/g, ' ')
    .trim();

  console.log('✨ Cleaned result:', cleanedText);
  return cleanedText;
};

// Helper function to wrap text with better Unicode handling
const wrapText = (doc, text, maxWidth, fontSize = 12) => {
  doc.setFontSize(fontSize);
  
  // Pre-process text to handle special characters better
  const processedText = text
    .replace(/\n/g, '\n')  // Ensure line breaks are preserved
    .replace(/\s+/g, ' ')  // Normalize spaces
    .trim();
  
  try {
    const lines = doc.splitTextToSize(processedText, maxWidth);
    return lines;
  } catch (error) {
    console.warn('Text wrapping error:', error);
    // Fallback: split manually if splitTextToSize fails
    const words = processedText.split(' ');
    const lines = [];
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const width = doc.getTextWidth(testLine);
      
      if (width <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  }
};

// Generate PDF for a single question
export const generateQuestionPDF = (questionData, topicInfo, questionNumber, moduleInfo) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Set up fonts for better Unicode support
  doc.setFont('helvetica');

  // Header
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  const headerText = `${moduleInfo?.name || '數學練習'} - ${topicInfo?.name || '練習題'}`;
  doc.text(headerText, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Question number and difficulty
  doc.setFontSize(14);
  doc.setTextColor(60, 60, 60);
  const questionTitle = `問題 ${questionNumber}`;
  doc.text(questionTitle, margin, yPosition);
  
  if (questionData.difficulty) {
    const difficultyMap = {
      'basic': '基礎',
      'intermediate': '中級', 
      'advanced': '高級'
    };
    const difficultyText = `難度: ${difficultyMap[questionData.difficulty] || questionData.difficulty}`;
    doc.text(difficultyText, pageWidth - margin, yPosition, { align: 'right' });
  }
  yPosition += 15;

  // Question content
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  
  // Main question
  if (questionData.question) {
    const questionText = cleanMathExpression(questionData.question);
    const questionLines = wrapText(doc, questionText, contentWidth, 12);
    questionLines.forEach(line => {
      if (yPosition > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
      doc.text(line, margin, yPosition);
      yPosition += 6;
    });
    yPosition += 5;
  }

  // Multi-part questions
  if (questionData.parts) {
    ['a', 'b', 'c'].forEach(part => {
      if (questionData.parts[part]) {
        if (yPosition > pageHeight - margin - 20) {
          doc.addPage();
          yPosition = margin;
        }

        // Part label
        doc.setFontSize(12);
        doc.setTextColor(0, 100, 200);
        doc.text(`(${part})`, margin, yPosition);
        yPosition += 8;

        // Part question
        doc.setTextColor(0, 0, 0);
        // Handle both data structures: M12 uses direct string, others use object with .question property
        const partQuestionText = typeof questionData.parts[part] === 'string' 
          ? questionData.parts[part] 
          : questionData.parts[part].question;
        const partQuestion = cleanMathExpression(partQuestionText);
        const partLines = wrapText(doc, partQuestion, contentWidth - 20, 11);
        partLines.forEach(line => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin + 15, yPosition);
          yPosition += 6;
        });
        yPosition += 8;
      }
    });
  }

  // Answer space
  yPosition += 10;
  doc.setFontSize(10);
  doc.setTextColor(150, 150, 150);
  doc.text('答案:', margin, yPosition);
  yPosition += 8;
  
  // Draw answer lines
  for (let i = 0; i < 5; i++) {
    if (yPosition > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 12;
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  const footer = `生成時間: ${new Date().toLocaleString('zh-TW')} | RightMaths 數學學習系統`;
  doc.text(footer, pageWidth / 2, pageHeight - 10, { align: 'center' });

  // Generate filename
  const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_Q${questionNumber}.pdf`;
  
  // Save the PDF
  doc.save(filename);
};

// Generate PDF for all questions in a topic
export const generateTopicPDF = (problems, topicInfo, moduleInfo) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Set up fonts
  doc.setFont('helvetica');

  // Cover page
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  const titleText = `${moduleInfo?.name || '數學練習'}`;
  doc.text(titleText, pageWidth / 2, pageHeight / 3, { align: 'center' });

  doc.setFontSize(16);
  const subtitleText = `${topicInfo?.name || '練習題集'}`;
  doc.text(subtitleText, pageWidth / 2, pageHeight / 3 + 20, { align: 'center' });

  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  const infoText = `共 ${problems.length} 題 | 生成時間: ${new Date().toLocaleString('zh-TW')}`;
  doc.text(infoText, pageWidth / 2, pageHeight / 3 + 40, { align: 'center' });

  // Add new page for questions
  doc.addPage();
  yPosition = margin;

  // Process each question
  problems.forEach((questionData, index) => {
    const questionNumber = index + 1;

    // Check if we need a new page
    if (yPosition > pageHeight - 100) {
      doc.addPage();
      yPosition = margin;
    }

    // Question header
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text(`問題 ${questionNumber}`, margin, yPosition);
    
    if (questionData.difficulty) {
      const difficultyMap = {
        'basic': '基礎',
        'intermediate': '中級',
        'advanced': '高級'
      };
      const difficultyText = `[${difficultyMap[questionData.difficulty] || questionData.difficulty}]`;
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(difficultyText, pageWidth - margin, yPosition, { align: 'right' });
    }
    yPosition += 12;

    // Main question
    if (questionData.question) {
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const questionText = cleanMathExpression(questionData.question);
      const questionLines = wrapText(doc, questionText, contentWidth, 11);
      questionLines.forEach(line => {
        if (yPosition > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
        doc.text(line, margin, yPosition);
        yPosition += 6;
      });
      yPosition += 3;
    }

    // Multi-part questions
    if (questionData.parts) {
      ['a', 'b', 'c'].forEach(part => {
        if (questionData.parts[part]) {
          if (yPosition > pageHeight - margin - 15) {
            doc.addPage();
            yPosition = margin;
          }

          // Part label
          doc.setFontSize(10);
          doc.setTextColor(0, 100, 200);
          doc.text(`(${part})`, margin + 5, yPosition);
          yPosition += 6;

          // Part question
          doc.setTextColor(0, 0, 0);
          // Handle both data structures: M12 uses direct string, others use object with .question property
          const partQuestionText = typeof questionData.parts[part] === 'string' 
            ? questionData.parts[part] 
            : questionData.parts[part].question;
          const partQuestion = cleanMathExpression(partQuestionText);
          const partLines = wrapText(doc, partQuestion, contentWidth - 30, 10);
          partLines.forEach(line => {
            if (yPosition > pageHeight - margin) {
              doc.addPage();
              yPosition = margin;
            }
            doc.text(line, margin + 20, yPosition);
            yPosition += 5;
          });
          yPosition += 3;
        }
      });
    }

    // Answer space for each question
    yPosition += 5;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('答案:', margin, yPosition);
    yPosition += 5;
    
    // Draw answer lines (fewer for topic PDF to save space)
    for (let i = 0; i < 2; i++) {
      if (yPosition > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;
    }
    
    yPosition += 10; // Space between questions
  });

  // Footer on last page
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  const footer = `RightMaths 數學學習系統 | 共 ${problems.length} 題`;
  doc.text(footer, pageWidth / 2, pageHeight - 10, { align: 'center' });

  // Generate filename
  const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_全部題目.pdf`;
  
  // Save the PDF
  doc.save(filename);
};

// Generate PDF with solutions (optional feature)
export const generateQuestionWithSolutionPDF = (questionData, topicInfo, questionNumber, moduleInfo) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Set up fonts
  doc.setFont('helvetica');

  // Header
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  const headerText = `${moduleInfo?.name || '數學練習'} - ${topicInfo?.name || '練習題'} (含解答)`;
  doc.text(headerText, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 20;

  // Question number and difficulty
  doc.setFontSize(14);
  doc.setTextColor(60, 60, 60);
  doc.text(`問題 ${questionNumber}`, margin, yPosition);
  yPosition += 15;

  // Question content (similar to generateQuestionPDF but with solutions)
  if (questionData.question) {
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    const questionText = cleanMathExpression(questionData.question);
    const questionLines = wrapText(doc, questionText, contentWidth, 12);
    questionLines.forEach(line => {
      if (yPosition > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
      doc.text(line, margin, yPosition);
      yPosition += 6;
    });
    yPosition += 10;
  }

  // Multi-part questions with solutions
  if (questionData.parts) {
    ['a', 'b', 'c'].forEach(part => {
      if (questionData.parts[part]) {
        if (yPosition > pageHeight - margin - 30) {
          doc.addPage();
          yPosition = margin;
        }

        // Part question
        doc.setFontSize(12);
        doc.setTextColor(0, 100, 200);
        doc.text(`(${part})`, margin, yPosition);
        yPosition += 8;

        doc.setTextColor(0, 0, 0);
        // Handle both data structures: M12 uses direct string, others use object with .question property
        const partQuestionText = typeof questionData.parts[part] === 'string' 
          ? questionData.parts[part] 
          : questionData.parts[part].question;
        const partQuestion = cleanMathExpression(partQuestionText);
        const partLines = wrapText(doc, partQuestion, contentWidth - 20, 11);
        partLines.forEach(line => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin + 15, yPosition);
          yPosition += 6;
        });
        yPosition += 5;

        // Solution
        const solutionData = typeof questionData.parts[part] === 'string' 
          ? questionData.solution?.[part] 
          : questionData.parts[part].solution;
          
        if (solutionData) {
          doc.setFontSize(10);
          doc.setTextColor(0, 150, 0);
          doc.text('解答:', margin + 15, yPosition);
          yPosition += 6;

          // Handle both data structures: M12 uses direct string, others use object with steps/answer
          if (typeof solutionData === 'string') {
            // M12 format: direct solution string
            const solutionText = cleanMathExpression(solutionData);
            const solutionLines = wrapText(doc, solutionText, contentWidth - 30, 9);
            solutionLines.forEach(line => {
              if (yPosition > pageHeight - margin) {
                doc.addPage();
                yPosition = margin;
              }
              doc.text(line, margin + 20, yPosition);
              yPosition += 5;
            });
          } else if (solutionData.steps && Array.isArray(solutionData.steps)) {
            // Other format: object with steps array
            solutionData.steps.forEach((step, stepIndex) => {
              const stepText = `${stepIndex + 1}. ${cleanMathExpression(step)}`;
              const stepLines = wrapText(doc, stepText, contentWidth - 30, 9);
              stepLines.forEach(line => {
                if (yPosition > pageHeight - margin) {
                  doc.addPage();
                  yPosition = margin;
                }
                doc.text(line, margin + 20, yPosition);
                yPosition += 5;
              });
            });
            
            if (solutionData.answer) {
              yPosition += 3;
              doc.setTextColor(0, 100, 0);
              const answerText = `答案: ${cleanMathExpression(solutionData.answer)}`;
              const answerLines = wrapText(doc, answerText, contentWidth - 30, 9);
              answerLines.forEach(line => {
                if (yPosition > pageHeight - margin) {
                  doc.addPage();
                  yPosition = margin;
                }
                doc.text(line, margin + 20, yPosition);
                yPosition += 5;
              });
            }
          }
        }
        yPosition += 10;
      }
    });
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  const footer = `生成時間: ${new Date().toLocaleString('zh-TW')} | RightMaths 數學學習系統`;
  doc.text(footer, pageWidth / 2, pageHeight - 10, { align: 'center' });

  // Generate filename
  const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_Q${questionNumber}_含解答.pdf`;
  
  // Save the PDF
  doc.save(filename);
}; 

// Generate PDF for all questions in a topic WITH solutions
export const generateTopicWithSolutionsPDF = (problems, topicInfo, moduleInfo) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = margin;

    // Title
    const title = `${moduleInfo?.name || '數學練習'} - ${topicInfo?.name || '練習題集'} (含詳細解答)`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    const titleLines = wrapText(doc, title, maxWidth);
    titleLines.forEach((line, index) => {
      if (yPosition > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
      doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;
    });

    yPosition += 10;

    // Process each problem
    problems.forEach((problemData, index) => {
      const questionNumber = index + 1;
      
      // Check if we need a new page
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = margin;
      }

      // Question number and difficulty
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      let questionHeader = `問題 ${questionNumber}`;
      if (problemData.difficulty) {
        questionHeader += ` (難度: ${problemData.difficulty})`;
      }
      doc.text(questionHeader, margin, yPosition);
      yPosition += 10;

      // Main question
      if (problemData.question) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        const questionText = cleanMathExpression(problemData.question);
        const questionLines = wrapText(doc, questionText, maxWidth);
        questionLines.forEach(line => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += 6;
        });
        yPosition += 5;
      }

      // Multi-part questions and solutions
      if (problemData.parts) {
        ['a', 'b', 'c'].forEach(partKey => {
          if (problemData.parts[partKey]) {
            const part = problemData.parts[partKey];
            
            // Check space for part
            if (yPosition > pageHeight - 40) {
              doc.addPage();
              yPosition = margin;
            }

            // Part label and question
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.text(`(${partKey})`, margin, yPosition);
            
            // Handle both data structures: M12 uses direct string, others use object with .question property
            const partQuestionText = typeof part === 'string' ? part : part.question;
            if (partQuestionText) {
              doc.setFont('helvetica', 'normal');
              const cleanedPartQuestion = cleanMathExpression(partQuestionText);
              const partQuestionLines = wrapText(doc, cleanedPartQuestion, maxWidth - 20);
              partQuestionLines.forEach((line, lineIndex) => {
                if (yPosition > pageHeight - margin) {
                  doc.addPage();
                  yPosition = margin;
                }
                const xPos = lineIndex === 0 ? margin + 15 : margin + 5;
                doc.text(line, xPos, yPosition);
                yPosition += 5;
              });
            }

            // Hint
            const hintText = typeof part === 'string' ? problemData.hint?.[partKey] : part.hint;
            if (hintText) {
              doc.setFont('helvetica', 'italic');
              doc.setFontSize(10);
              const cleanedHintText = `提示: ${cleanMathExpression(hintText)}`;
              const hintLines = wrapText(doc, cleanedHintText, maxWidth - 10);
              hintLines.forEach(line => {
                if (yPosition > pageHeight - margin) {
                  doc.addPage();
                  yPosition = margin;
                }
                doc.text(line, margin + 5, yPosition);
                yPosition += 4;
              });
              yPosition += 3;
            }

            // Solution
            const solutionData = typeof part === 'string' ? problemData.solution?.[partKey] : part.solution;
            if (solutionData) {
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(10);
              doc.text(`解答 (${partKey}):`, margin + 5, yPosition);
              yPosition += 5;

              doc.setFont('helvetica', 'normal');
              
              // Handle both data structures: M12 uses direct string, others use object with steps/answer
              if (typeof solutionData === 'string') {
                // M12 format: direct solution string
                const cleanedSolutionText = cleanMathExpression(solutionData);
                const solutionLines = wrapText(doc, cleanedSolutionText, maxWidth - 15);
                solutionLines.forEach(line => {
                  if (yPosition > pageHeight - margin) {
                    doc.addPage();
                    yPosition = margin;
                  }
                  doc.text(line, margin + 10, yPosition);
                  yPosition += 4;
                });
              } else {
                // Other format: object with steps array
                if (solutionData.steps && solutionData.steps.length > 0) {
                  solutionData.steps.forEach((step, stepIndex) => {
                    const stepText = `${stepIndex + 1}. ${cleanMathExpression(step)}`;
                    const stepLines = wrapText(doc, stepText, maxWidth - 15);
                    stepLines.forEach(line => {
                      if (yPosition > pageHeight - margin) {
                        doc.addPage();
                        yPosition = margin;
                      }
                      doc.text(line, margin + 10, yPosition);
                      yPosition += 4;
                    });
                  });
                  yPosition += 2;
                }

                // Final answer
                if (solutionData.answer) {
                  doc.setFont('helvetica', 'bold');
                  const answerText = `答案: ${cleanMathExpression(solutionData.answer)}`;
                  const answerLines = wrapText(doc, answerText, maxWidth - 15);
                  answerLines.forEach(line => {
                    if (yPosition > pageHeight - margin) {
                      doc.addPage();
                      yPosition = margin;
                    }
                    doc.text(line, margin + 10, yPosition);
                    yPosition += 5;
                  });
                }
              }
            }

            yPosition += 8;
          }
        });
      }

      yPosition += 15; // Space between questions
    });

    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(
        `RightMaths 數學學習系統 | 共 ${problems.length} 題 (含詳細解答) | 第 ${i} 頁，共 ${totalPages} 頁`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save the PDF
    const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_全部題目含解答.pdf`;
    doc.save(filename);
    
  } catch (error) {
    console.error('Error generating topic with solutions PDF:', error);
    throw error;
  }
}; 